import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Coins, BookOpen, Star, CalendarDays, MousePointerClick, X } from "lucide-react";

const GOLD = "#E6B35A";
const RED = "#C8000F";

const TIPS = [
  { Icon: MousePointerClick, title: "Pick your preference", body: "Tap + under a performance to set it as your 1st pick. Tap a second day for your 2nd (back-up)." },
  { Icon: Star, title: "Watch a rodeo", body: "Open any rodeo and tap the ★ Watch button — we'll send entry-open & closing reminders to your bell (top-right)." },
  { Icon: CalendarDays, title: "Jump to a date", body: "Use the calendar icon (top-right) to filter to a single day. Today is always marked in gold." },
  { Icon: Coins, title: "Read the value", body: "Green $/mi is payout value per mile of travel; added money shows the purse for your events." },
];

// One-time welcome card on the mobile Table view. Dismissed per user (localStorage).
export default function TableTutorial() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const key = user ? `rp_table_tut_${user.id}` : null;

  useEffect(() => {
    if (!key) return;
    if (localStorage.getItem(key)) return;
    let cancelled = false;
    // Wait until the first-run Onboarding modal is dismissed before showing the
    // tutorial — otherwise this sheet (z-4000) overlays the onboarding (z-3000)
    // and swallows taps on its "Start planning" button.
    const tryShow = () => {
      if (cancelled) return;
      if (document.querySelector('[data-testid="onboarding-modal"]')) { setTimeout(tryShow, 800); return; }
      setShow(true);
    };
    const t = setTimeout(tryShow, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [key]);

  const close = () => { if (key) localStorage.setItem(key, "1"); setShow(false); };
  const openGuide = () => { close(); nav("/guide"); };

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[4000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="table-tutorial">
      <div className="w-full sm:max-w-md bg-[#121823] border-t sm:border border-white/10 rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl animate-[rpUp_0.25s_ease]">
        <div className="relative px-5 pt-5 pb-4" style={{ background: "linear-gradient(135deg,#1A1205,#141A24)" }}>
          <button onClick={close} data-testid="table-tut-close" aria-label="Close" className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"><X className="w-5 h-5" /></button>
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: RED }}>Welcome to Rodeo Pilot</div>
          <h2 className="font-display text-2xl font-extrabold text-white mt-1">Get going in 4 quick steps</h2>
          <p className="text-sm text-slate-400 mt-1">Your season, performance-by-performance. Here's the gist.</p>
        </div>
        <div className="p-4 space-y-3 max-h-[52vh] overflow-y-auto">
          {TIPS.map((t, i) => {
            const Icon = t.Icon;
            return (
              <div key={i} className="flex gap-3" data-testid={`table-tut-tip-${i}`}>
                <span className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(230,179,90,0.12)" }}><Icon className="w-5 h-5" style={{ color: GOLD }} /></span>
                <div>
                  <div className="font-semibold text-slate-100 text-sm">{t.title}</div>
                  <div className="text-[13px] text-slate-400 leading-snug mt-0.5">{t.body}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-4 pt-1 flex flex-col gap-2">
          <button onClick={openGuide} data-testid="table-tut-guide" className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl text-white" style={{ background: RED }}>
            <BookOpen className="w-4 h-4" /> View full guide
          </button>
          <button onClick={close} data-testid="table-tut-gotit" className="w-full font-semibold py-2.5 rounded-xl text-slate-300 bg-white/5 hover:bg-white/10">Got it</button>
        </div>
      </div>
    </div>
  );
}
